import { useEffect, useRef, useState } from 'react';
import organizationService from '../../services/organization';
import Button from '../ui/Button';

const MAX_BYTES = 2 * 1024 * 1024;

interface Props {
  orgId: number;
  /** From the organisation record; changes whenever the logo does. */
  logoUpdatedAt?: string | null;
}

/**
 * The logo printed at the top of the organisation's invoices, credit notes,
 * statements, delivery dockets, pick lists and run sheets. PNG or JPEG only:
 * those are what the PDFs can draw, and the server refuses anything else.
 */
export default function OrganisationLogo({ orgId, logoUpdatedAt }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [version, setVersion] = useState(logoUpdatedAt ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  // 'missing': the record says there is a logo and it could not be fetched.
  const [state, setState] = useState<'loading' | 'shown' | 'missing'>('loading');

  useEffect(() => {
    setUrl(null);
    if (!version) return;
    setState('loading');
    let objectUrl: string | null = null;
    let live = true;
    organizationService.getLogo(orgId).then((blob) => {
      if (!live) return;
      if (!blob) {
        setState('missing');
        return;
      }
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
      setState('shown');
    });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [orgId, version]);

  const upload = async (file: File) => {
    setError(null);
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Choose a PNG or JPEG image.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('The logo must be 2 MB or smaller.');
      return;
    }
    setBusy(true);
    try {
      const res = await organizationService.uploadLogo(orgId, file);
      setVersion((res.success && res.data?.logo_updated_at) || new Date().toISOString());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The logo could not be saved.');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const remove = async () => {
    setError(null);
    setBusy(true);
    try {
      await organizationService.removeLogo(orgId);
      setVersion(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'The logo could not be removed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Logo</h3>
        <p className="text-xs text-gray-500 mt-1">
          Printed at the top of your invoices, credit notes, statements, delivery dockets, pick lists and run sheets.
          A PNG or JPEG up to 2 MB. A wide logo on a white or clear background prints best.
        </p>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex h-20 w-full sm:w-64 items-center justify-center rounded-lg border border-gray-200 bg-white p-2">
          {url ? (
            <img src={url} alt="Organisation logo" className="max-h-full max-w-full object-contain" />
          ) : (
            <span className="text-xs text-gray-400 text-center">
              {!version ? 'No logo. Your name prints instead.'
                : state === 'missing' ? 'The logo could not be shown here. Upload it again if it is missing from your paperwork.'
                  : 'Loading…'}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <Button variant="outline" size="sm" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? 'Saving…' : version ? 'Replace logo' : 'Upload logo'}
          </Button>
          {version && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={remove}>
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
